import type {ComponentPropsWithRef} from 'react';
import React from 'react';
import mergeRefs from 'merge-refs';
import {withCssModule} from '@pb/kit-util-with-css-module';

import {ALIGN_CLASSES, resolveAlign} from './constants';
import type {VerticalAlignType} from './constants';

import styles from './table.module.styl';

const classNames = withCssModule(styles);

export type TableProps = Omit<ComponentPropsWithRef<'table'>, 'align'> & {
    /**
     * Make table layout to FIXED
     * @default true
     */
    fixed?: boolean,
    /**
     * Border for all cells of table
     * @default false
     */
    borderedGrid?: boolean,
    /**
     * If true, table will have borders between columns
     */
    borderedColumns?: boolean,
    /**
     * If true, table rows will have height and padding like in data table
     */
    dataTableRows?: boolean,
    /**
     * Vertical alignment of table cells
     */
    align?: 'default' | 'top' | 'middle' | 'baseline' | 'bottom',
    /**
     * Table vertical align
     * @deprecated Use `align` instead. It will be removed in 6 months 03-03-2026.
     */
    verticalAlign?: VerticalAlignType,
    /**
     * Add extra class names (BEM mix pattern)
     * @deprecated Use `className` instead. It will be removed in 6 months 03-03-2026.
     */
    mix?: string,
    /**
     * Callback for setting table node as ref
     * @deprecated Use `ref` instead. It will be removed in 6 months 03-03-2026.
     */
    getRefTable?: (e: HTMLTableElement | null) => void,
};

export const Table = ({
    fixed = true,
    borderedGrid = false,
    borderedColumns,
    dataTableRows,
    align,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    verticalAlign,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    mix,
    className,
    children,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    getRefTable,
    ref,
    ...rest
}: TableProps) => {
    const tableClassName = classNames(
        'table',
        mix,
        className,
        {
            'table_fixed': fixed,
            'table_bordered-grid': borderedGrid,
            'table_bordered-columns': borderedColumns,
            'table_data-table-rows': dataTableRows,
        },
        ALIGN_CLASSES('table')[resolveAlign(align, verticalAlign)],
    );

    return (
        <table
            {...rest}
            ref={mergeRefs(ref, getRefTable)}
            className={tableClassName}>
            {children}
        </table>
    );
};
